import { useOutletContext } from "react-router-dom";
import { IntegrationShell } from "../../common/Shell";
import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import { useEffect, useMemo, useState } from "react";
import { useAssistants } from "V2.0/HomePages/Assistants";
import CopyInput from "V2.0/common/CopyInput";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import TataSmartflow from "V2.0/images/tata-smart-flo.png";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

function TataSmartflo(props) {
  const {
    workspaceId,
    tataSmartFloSettings,
    updateTataSmartFloSettings,
    fetchThirdPartyData,
    isViewOnly,
  } = props;

  const [phoneNumbersAndAuth, setPhoneNumbersAndAuth] = useState([
    {
      phoneNumber: "",
      phoneNumberAuthKey: "",
    },
  ]);
  const [authKey, setAuthKey] = useState(tataSmartFloSettings?.authKey ?? "");

  const [errors, setErrors] = useState({
    authKey: null,
    phoneNumbersAndAuth: [],
  });

  useEffect(() => {
    if (tataSmartFloSettings) {
      setAuthKey(tataSmartFloSettings?.authKey ?? "");

      const transformedPhoneNumbersAndAuth = Object.keys(
        tataSmartFloSettings?.phoneNumbersAndAuth,
      ).map((phoneNumber) => {
        return {
          phoneNumber,
          phoneNumberAuthKey:
            tataSmartFloSettings?.phoneNumbersAndAuth[phoneNumber],
        };
      });

      if (transformedPhoneNumbersAndAuth.length > 0) {
        setPhoneNumbersAndAuth(transformedPhoneNumbersAndAuth);
      }
    }
  }, [tataSmartFloSettings]);

  function addPhoneNumber() {
    setPhoneNumbersAndAuth((prevState) => {
      return [...prevState, { phoneNumber: "", phoneNumberAuthKey: "" }];
    });
  }

  function removePhoneNumbersAndAuth(idx) {
    setPhoneNumbersAndAuth((prevState) => {
      const updatedState = prevState.toSpliced(idx, 1);
      return updatedState;
    });
  }

  function handleUpdatePhoneNumber(idx, phoneNumber) {
    setPhoneNumbersAndAuth((prevState) => {
      const updatedState = prevState.toSpliced(idx, 1, {
        phoneNumber,
        phoneNumberAuthKey: prevState[idx].phoneNumberAuthKey,
      });
      return updatedState;
    });
  }

  function handleUpdatePhoneAuthKey(idx, phoneNumberAuthKey) {
    setPhoneNumbersAndAuth((prevState) => {
      const updatedState = prevState.toSpliced(idx, 1, {
        phoneNumberAuthKey,
        phoneNumber: prevState[idx].phoneNumber,
      });
      return updatedState;
    });
  }

  function handleConnect() {
    const lifecycleCbs = {
      init: () => {
        message.loading("Connecting");
      },
      success: () => {
        message.destroy();
        message.success("Connected");
      },
      error: (e) => {
        message.destroy();
        showErrorToast(e, "Failed to Connect");
      },
    };

    const payload = {
      telephonySettings: {
        telephonyProvider: "tata",
        tataSmartFloSettings: {
          authKey,
          phoneNumbersAndAuth: phoneNumbersAndAuth.reduce((acc, val) => {
            return {
              ...acc,
              [val.phoneNumber]: val.phoneNumberAuthKey,
            };
          }, {}),
        },
      },
    };

    updateTataSmartFloSettings(payload, workspaceId, lifecycleCbs);
  }

  const isConnectDisabled =
    !authKey.trim() ||
    phoneNumbersAndAuth.some(
      ({ phoneNumber, phoneNumberAuthKey }) =>
        !phoneNumber.trim() || !phoneNumberAuthKey.trim(),
    );

  return (
    <div className="border rounded-lg p-8 space-y-8 mt-8 w-full">
      <div className="flex items-center gap-x-2 mb-4">
        <img
          src={TataSmartflow}
          width={60}
        />
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          Connect Tata Smartflo
        </h3>
      </div>
      <InputComponent
        type="text"
        label="API Token *"
        onChange={(e) => setAuthKey(e.target.value)}
        value={authKey}
        disabled={isViewOnly}
      />
      <div className="space-y-2">
        <div className="grid grid-cols-2 space-x-2">
          <p className="text-sm">Phone Number *</p>
          <p className="text-sm">Phone Number API Key *</p>
        </div>

        {phoneNumbersAndAuth.map((phoneNumberAndAuth, idx) => {
          return (
            <div
              key={idx}
              className="grid grid-cols-2 space-x-2"
            >
              <div className="col-span-1 mr-4">
                <InputComponent
                  type="text"
                  value={phoneNumberAndAuth.phoneNumber}
                  onChange={(e) => handleUpdatePhoneNumber(idx, e.target.value)}
                  disabled={isViewOnly}
                />
              </div>
              <div className="col-span-1 items-center w-full">
                <div className="flex items-center gap-x-2">
                  <InputComponent
                    type="text"
                    value={phoneNumberAndAuth.phoneNumberAuthKey}
                    onChange={(e) =>
                      handleUpdatePhoneAuthKey(idx, e.target.value)
                    }
                    className="flex-1 w-full"
                    disabled={isViewOnly}
                  />
                  {phoneNumbersAndAuth.length !== 1 && !isViewOnly && (
                    <XMarkIcon
                      onClick={() => removePhoneNumbersAndAuth(idx)}
                      className="size-4"
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <Button
          plain
          disabled={isViewOnly}
          onClick={addPhoneNumber}
        >
          <PlusIcon /> Add Phone number
        </Button>
      </div>
      {!isViewOnly && (
        <div className="w-full">
          <Button
            disabled={isConnectDisabled}
            className="w-full"
            onClick={handleConnect}
          >
            Connect
          </Button>
        </div>
      )}
    </div>
  );
}

export default function TataSmartfloIntegration() {
  const {
    projectId,
    thirdpartyData,
    isThirdPartyLoading,
    updateThirdpartyData,
    fetchThirdpartyData,
    isViewOnly,
  } = useOutletContext();

  if (isThirdPartyLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <AiOutlineLoading3Quarters className="animate-spin" />
      </div>
    );
  }

  return (
    <IntegrationShell>
      <TataSmartflo
        workspaceId={projectId}
        tataSmartFloSettings={
          thirdpartyData?.telephonySettings?.tataSmartFloSettings
        }
        updateTataSmartFloSettings={updateThirdpartyData}
        fetchThirdpartyData={fetchThirdpartyData}
        isViewOnly={isViewOnly}
      />
    </IntegrationShell>
  );
}
